import React, { useState, useEffect, useCallback, ChangeEvent, SyntheticEvent, useMemo } from 'react';
import {
  useTheme, useMediaQuery, Typography, TextField, Button, Box, Grid, FormControl, MenuItem, InputLabel, Select, CardContent, Divider,
  Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, CircularProgress,
  Accordion, AccordionSummary, AccordionDetails,
} from '@mui/material';
import { NavLink, useNavigate } from 'react-router-dom';
import axios from "axios";
import { Save as SaveIcon, AddBox as AddBoxIcon, Autorenew as AutorenewIcon, ExpandMore as ExpandMoreIcon } from '@mui/icons-material';

import Title from '../../../components/Title';
import { Office } from '../../../models/office';
import { Collect } from '../../../models/collect';
import { useSelection } from '../../../components/context/SelectionContext';
import ExpenseCollect from './modal/expenseCollect';
import { formatCurrency } from '../../../utils/format/currency';
import ConfirmationModal from './modal/confirmCloser';
import moment from 'moment-timezone';
import SimpleCustomTable from '../../../components/table/SimpleCustomTable';
import { SimpleColumn } from '../../../components/table/interface';
import HeaderResponsive from '../../../components/layouts/HeaderResponsive';
import PageScrollArea from '../../../components/layouts/PageScrollArea';
import PermissionButton from '../../../components/common/PermissionButton';
import { useResourcePermissions } from '../../../hooks/useResourcePermissions';

/** Compatible con navegadores sin Promise.allSettled (p. ej. Safari antes de 13, Chrome antes de 76). */
function promiseAllSettled<T>(
  promises: Array<PromiseLike<T>>,
): Promise<Array<{ status: 'fulfilled'; value: T } | { status: 'rejected'; reason: unknown }>> {
  return Promise.all(
    promises.map((p) =>
      Promise.resolve(p).then(
        (value) => ({ status: 'fulfilled' as const, value }),
        (reason) => ({ status: 'rejected' as const, reason }),
      ),
    ),
  );
}

interface ShowAlerts {
  showAlert: (message: string, severity?: 'success' | 'info' | 'warning' | 'error') => void;
  showLoading: () => void;
  hideLoading: () => void;
}
interface ExpenseItem {
  id: number;
  date: string;
  description: string;
  value: number;
  balance: number;
  expense: {
    id: number;
    name: string;
    box_adjust: boolean;
    type_operation?: {
      id: number;
      name: string;
    };
  };
}
interface CreatedExpenseDisplayItem {
  id: number;
  expense_id: number;
  value: number;
  description: string;
  name: string;
  boxId: number;
}

/** True si el cierre y la lista de gastos indican que ese día no hubo actividad operativa. */
function liquidationDayHasNoMovements(
  closer: Record<string, unknown> | null,
  expensesPayload: unknown,
): boolean {
  const tuple = expensesPayload as [ExpenseItem[], number] | undefined;
  const rawExpenseList = tuple?.[0];
  const expenseList = Array.isArray(rawExpenseList) ? rawExpenseList : [];
  const hasExpenseRows = expenseList.length > 0;

  const n = (v: unknown) => {
    const x = Number(v);
    return Number.isFinite(x) ? x : 0;
  };

  if (!closer) {
    return !hasExpenseRows;
  }

  const hasCloserActivity =
    n(closer.count_pays) > 0 ||
    n(closer.count_lends) > 0 ||
    n(closer.total_pays) > 0 ||
    n(closer.total_lends) > 0 ||
    n(closer.total_expenses) > 0 ||
    n(closer.total_off_exp_plus) > 0 ||
    n(closer.total_off_exp_minus) > 0 ||
    n(closer.total_discounts) > 0 ||
    n(closer.value_refinance) > 0 ||
    n(closer.total_refinance) > 0 ||
    n(closer.total_utility) > 0 ||
    n(closer.total_insurance_charge) > 0;

  return !hasExpenseRows && !hasCloserActivity;
}

const LiquidacionCobros: React.FC<ShowAlerts> = ({ showAlert, showLoading, hideLoading }) => {
  document.title = "Liquidar Cobro";
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();
  const { canCreate, canUpdate, canDelete, canRegisterExpenses } =
    useResourcePermissions('/procesos/liquidacioncobros');

  // Estados
  const [collectsOffice, setCollectsOffice] = useState<Collect[]>([]);
  const [offices, setOffices] = useState<Office[]>([]);
  const [box_id, setBox_id] = useState<number>(0);
  const [route_id, setRouteId] = useState<number | null>(null);
  const [total_off_exp_plus, setTotalOfficePlus] = useState<number>(0);
  const [total_off_exp_minus, setTotalOfficeMinus] = useState<number>(0);
  const [total_utility, setTotalUtility] = useState<number>(0);
  const [totalInsuranceCharge, setTotalInsuranceCharge] = useState<number>(0);
  const [utility_refinance, setUtilityRefinance] = useState<number>(0);
  const [value_refinance, setValueRefinance] = useState<number>(0);
  const [value_box, setValueBox] = useState<number>(0);
  const [valuePay, setValuePay] = useState(0);
  const [countPay, setCountPay] = useState(0);
  const [valueLend, setValueLend] = useState(0);
  const [countLend, setCountLend] = useState(0);
  const [valuePayCancel, setValuePayCancel] = useState(0);
  const [cashShortOver, setCashShortOver] = useState(0);
  const [totalExpenses, setTotalExpenses] = useState(0);
  const [totalDiscount, setTotalDiscount] = useState(0);
  const [valueLendCancel, setValueLendCancel] = useState(0);
  const [countLendCancel, setCountLendCancel] = useState(0);
  const [countClient, setCountClient] = useState(0);
  const [valueClientClove, setValueClientClove] = useState(0);
  const [countClientClove, setCountClientClove] = useState(0);
  const [percenCollectRoute, setPercenCollectRoute] = useState(0);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalOpenConfirm, setModalConfirm] = useState(false);
  const [listExpenses, setListExpenses] = useState<[ExpenseItem[], number] | null>(null);
  const [deletingExpenseId, setDeletingExpenseId] = useState<number | null>(null);
  const [base, setBase] = useState<string>("0");
  const [cash, setCash] = useState<string>("0");
  const [activeSearch, setActiveSearch] = useState<{ collectId: string | number; date: string } | null>(null);
  const [reliquidating, setReliquidating] = useState(false);
  const [isResolvingBox, setIsResolvingBox] = useState(false);
  const [expensesAccordionOpen, setExpensesAccordionOpen] = useState(true);
  const [dateWithoutMovements, setDateWithoutMovements] = useState(false);
  const { office_id, setOffice_id, collect_id, setCollect_id, user_id, setUser_id } = useSelection();
  const [currentDate, setCurrentDate] = useState(() => {
    return moment().tz("America/Bogota").format("YYYY-MM-DD"); // valor inicial: hoy
  });
  const [timeDate] = useState(() => {
    return moment().tz("America/Bogota").format("HH:mm:ss");
  });
  const normalizeExpenseDate = (rawDate: string) => {
    return moment.tz(rawDate, "America/Bogota").format("YYYY-MM-DD");
  };
  const parseLocalizedNumber = (value: unknown): number | null => {
    if (value === null || value === undefined) return null;
    if (typeof value === 'number') return Number.isFinite(value) ? value : null;

    const raw = String(value).trim();
    if (!raw) return null;

    const normalized = raw
      .replace(/\s+/g, '')
      .replace(/\$/g, '')
      .replace(/[^0-9,.\-]/g, '');

    if (!normalized || normalized === '-' || normalized === ',' || normalized === '.') {
      return null;
    }

    const esFormat = /^-?\d{1,3}(\.\d{3})+(,\d+)?$/;
    const enFormat = /^-?\d{1,3}(,\d{3})+(\.\d+)?$/;
    const decimalComma = /^-?\d+,\d+$/;
    const decimalDot = /^-?\d+(\.\d+)?$/;

    if (esFormat.test(normalized)) {
      const parsed = Number(normalized.replace(/\./g, '').replace(',', '.'));
      return Number.isFinite(parsed) ? parsed : null;
    }
    if (enFormat.test(normalized)) {
      const parsed = Number(normalized.replace(/,/g, ''));
      return Number.isFinite(parsed) ? parsed : null;
    }
    if (decimalComma.test(normalized)) {
      const parsed = Number(normalized.replace(',', '.'));
      return Number.isFinite(parsed) ? parsed : null;
    }
    if (decimalDot.test(normalized)) {
      const parsed = Number(normalized);
      return Number.isFinite(parsed) ? parsed : null;
    }

    const sign = normalized.startsWith('-') ? -1 : 1;
    const digits = normalized.replace(/\D/g, '');
    if (!digits) return null;

    const parsed = Number(digits);
    return Number.isFinite(parsed) ? sign * parsed : null;
  };
  const baseNumber = useMemo(() => parseLocalizedNumber(base) ?? 0, [base]);
  const cashNumber = useMemo(() => parseLocalizedNumber(cash) ?? 0, [cash]);
  const isBaseNonZero = useMemo(() => baseNumber !== 0, [baseNumber]);
  const isCashNonZero = useMemo(() => cashNumber !== 0, [cashNumber]);
  const canSearch = Boolean(office_id) && Boolean(collect_id);
  const canOperateWithBox = canSearch && box_id > 0 && !isResolvingBox;
  const activeCollectId = activeSearch?.collectId;
  const activeDate = activeSearch?.date;
  const hasActiveSearch = Boolean(activeSearch);
  const compactTableCellSx = useMemo(
    () => ({
      border: '1px solid rgba(224, 224, 224, 1)',
      py: isMobile ? 0.5 : 0.75,
      px: isMobile ? 0.5 : 1.25,
      fontSize: isMobile ? '0.68rem' : '0.8125rem',
      lineHeight: 1.25,
      wordBreak: 'break-word' as const,
      overflowWrap: 'break-word' as const,
      verticalAlign: 'top' as const,
    }),
    [isMobile],
  );
  const compactTableLabelSx = useMemo(
    () => ({
      ...compactTableCellSx,
      fontWeight: 'bold' as const,
    }),
    [compactTableCellSx],
  );
  // Efectos
  useEffect(() => {

    const fetchData = async () => {
      showLoading();
      try {
        const [officesResponse, collectsOfficeResponse] = await Promise.all([
          axios.get('offices'),
          office_id ? axios.get(`collectsoffice/${office_id}`) : Promise.resolve(null)
        ]);

        const officeList = officesResponse.data?.data ?? officesResponse.data ?? [];
        const officesOk = Array.isArray(officeList) ? officeList : [];
        setOffices(officesOk);
        if (officesOk.length === 0) {
          showAlert(
            'No hay oficinas disponibles o no tiene permisos para verlas. Verifique la configuración o contacte a soporte.',
            'warning',
          );
        }

        const [{ data: userData }] = await Promise.all([axios.get('user'),]);
        setUser_id(userData.data.id);

        if (collectsOfficeResponse) {
          const rawCollects =
            collectsOfficeResponse.data?.data ?? collectsOfficeResponse.data ?? [];
          const collectsOk = Array.isArray(rawCollects) ? rawCollects : [];
          setCollectsOffice(collectsOk);
          if (officesOk.length > 0 && office_id && collectsOk.length === 0) {
            showAlert(
              'La oficina seleccionada no tiene cobros asociados o no existen cobros visibles para su usuario.',
              'warning',
            );
            setCollect_id('');
          }
        } else {
          setCollectsOffice([]);
        }
      } catch (error) {
        showAlert('No se pudieron cargar oficinas o cobros. Revise la conexión e intente de nuevo.', 'error');
      } finally {
        hideLoading();
      }
    };

    fetchData();
  }, [office_id]);

  const resetSearchData = () => {
    setBox_id(0);
    setRouteId(null);
    setTotalOfficePlus(0);
    setTotalOfficeMinus(0);
    setTotalUtility(0);
    setTotalInsuranceCharge(0);
    setUtilityRefinance(0);
    setValueRefinance(0);
    setValueBox(0);
    setValuePay(0);
    setCountPay(0);
    setValueLend(0);
    setCountLend(0);
    setValuePayCancel(0);
    setTotalExpenses(0);
    setTotalDiscount(0);
    setValueLendCancel(0);
    setCountLendCancel(0);
    setCountClient(0);
    setValueClientClove(0);
    setCountClientClove(0);
    setPercenCollectRoute(0);
    setListExpenses(null);
    setBase("0");
    setCash("0");
    setCashShortOver(0);
    setDateWithoutMovements(false);
  };

  const applyCloserData = (
    closerRecord: any | null,
    options?: { preserveManualFields?: boolean },
  ) => {
    const preserveManualFields = Boolean(options?.preserveManualFields);

    if (!closerRecord) {
      setTotalOfficePlus(0);
      setTotalOfficeMinus(0);
      setTotalUtility(0);
      setTotalInsuranceCharge(0);
      setUtilityRefinance(0);
      setValueRefinance(0);
      setValueBox(0);
      setValuePay(0);
      setCountPay(0);
      setValueLend(0);
      setCountLend(0);
      setValuePayCancel(0);
      setTotalExpenses(0);
      setTotalDiscount(0);
      setValueLendCancel(0);
      setCountLendCancel(0);
      setCountClient(0);
      setValueClientClove(0);
      setCountClientClove(0);
      setPercenCollectRoute(0);
      if (!preserveManualFields) {
        setBase("0");
        setCash("0");
        setCashShortOver(0);
      }
      return;
    }

    if (!preserveManualFields) {
      setBase(String(closerRecord.base ?? 0));
      setCash(String(closerRecord.cash ?? 0));
      setCashShortOver(Number(closerRecord.cash_short_over ?? 0));
    }
    setTotalOfficePlus(Number(closerRecord.total_off_exp_plus ?? 0));
    setTotalOfficeMinus(Number(closerRecord.total_off_exp_minus ?? 0));
    setTotalUtility(Number(closerRecord.total_utility ?? 0));
    setTotalInsuranceCharge(Number(closerRecord.total_insurance_charge ?? 0));
    setUtilityRefinance(Number(closerRecord.total_refinance ?? 0));
    setValueRefinance(Number(closerRecord.value_refinance ?? 0));
    setValueBox(Number(closerRecord.value_box ?? 0));
    setValuePay(Number(closerRecord.total_pays ?? 0));
    setCountPay(Number(closerRecord.count_pays ?? 0));
    setValueLend(Number(closerRecord.total_lends ?? 0));
    setCountLend(Number(closerRecord.count_lends ?? 0));
    setValuePayCancel(Number(closerRecord.total_cancel ?? 0));
    setTotalExpenses(Number(closerRecord.total_expenses ?? 0));
    setTotalDiscount(Number(closerRecord.total_discounts ?? 0));
    setValueLendCancel(Number(closerRecord.total_cancel ?? 0));
    setCountLendCancel(Number(closerRecord.count_cancel ?? 0));
    setCountClient(Number(closerRecord.count_client ?? 0));
    setValueClientClove(Number(closerRecord.value_client_clove ?? 0));
    setCountClientClove(Number(closerRecord.clove_client ?? 0));
    setPercenCollectRoute(Number(closerRecord.percentage_route ?? 0));
  };

  const loadCloserByDate = async ({
    collectId,
    boxId,
    date,
  }: {
    collectId: string | number;
    boxId: number;
    date: string;
  }) => {
    if (!boxId) {
      return null;
    }

    const normalizedDate = normalizeExpenseDate(date);
    const { data } = await axios.post('/process/closerbydate', {
      collect_id: Number(collectId),
      box_id: boxId,
      date: normalizedDate,
    });

    return data?.data || null;
  };

  const ensureBoxForCollect = async (collectId: string | number): Promise<number> => {
    const parsedCollectId = Number(collectId);
    if (!Number.isInteger(parsedCollectId) || parsedCollectId <= 0) {
      return 0;
    }

    setIsResolvingBox(true);
    try {
      const boxIdResponse = await axios.get(`/boxcollect/${parsedCollectId}`);
      const boxes = boxIdResponse.data?.data ?? boxIdResponse.data;
      const firstBox = Array.isArray(boxes) ? boxes[0] : null;
      const resolvedBoxId = Number(firstBox?.id || 0);
      setBox_id(resolvedBoxId);
      return resolvedBoxId;
    } catch (error) {
      console.error('Error resolviendo box_id:', error);
      setBox_id(0);
      return 0;
    } finally {
      setIsResolvingBox(false);
    }
  };

  const fetchSearchData = async (
    params: { collectId: string | number; date: string },
    attempt = 0,
  ): Promise<void> => {
    showLoading();
    setDateWithoutMovements(false);
    try {
      const requestDate = normalizeExpenseDate(params.date);
      const boxIdResponse = await axios.get(`/boxcollect/${params.collectId}`);
      const boxes = boxIdResponse.data?.data ?? boxIdResponse.data;
      const firstBox = Array.isArray(boxes) ? boxes[0] : null;
      const newBoxId = Number(firstBox?.id || 0);
      setBox_id(newBoxId);

      if (!newBoxId) {
        applyCloserData(null);
        setListExpenses([[], 0]);
        setRouteId(null);
        showAlert(
          'No se encontró la caja del cobro (box). Verifique permisos del usuario o contacte soporte.',
          'error',
        );
        return;
      }

      const [closerResponse, expensesResponse, routeCollectResponse] = await promiseAllSettled([
        loadCloserByDate({
          collectId: params.collectId,
          boxId: newBoxId,
          date: requestDate,
        }),
        axios.post(`/process/findexpensesbox`, {
          collect_id: params.collectId,
          date: requestDate,
        }),
        axios.get(`/routecollect/${params.collectId}`),
      ]);

      if (closerResponse.status === 'fulfilled') {
        applyCloserData(closerResponse.value);
        if (closerResponse.value === null) {
          showAlert(
            'Aún no hay cierre guardado para esta fecha. Los totales pueden verse en cero. Use «Reliquidar» para generar o actualizar el cierre del día.',
            'info',
          );
        }
      } else {
        console.error('Error cargando closer:', closerResponse.reason);
        applyCloserData(null);
        showAlert(
          'No se pudo cargar el cierre del día. Revise conexión o intente de nuevo. Si persiste, use «Reliquidar».',
          'warning',
        );
      }

      if (expensesResponse.status === 'fulfilled') {
        setListExpenses(expensesResponse.value.data);
      } else {
        console.error('Error cargando gastos:', expensesResponse.reason);
        setListExpenses([[], 0]);
        showAlert('No se pudieron cargar los gastos de caja para esta fecha.', 'warning');
      }

      if (routeCollectResponse.status === 'fulfilled') {
        const activeRoute = Array.isArray(routeCollectResponse.value.data?.data)
          ? routeCollectResponse.value.data.data[0]
          : null;
        setRouteId(activeRoute?.id ? Number(activeRoute.id) : null);
      } else {
        console.error('Error cargando ruta activa:', routeCollectResponse.reason);
        setRouteId(null);
      }

      if (closerResponse.status === 'fulfilled' && expensesResponse.status === 'fulfilled') {
        setDateWithoutMovements(
          liquidationDayHasNoMovements(closerResponse.value, expensesResponse.value.data),
        );
      } else {
        setDateWithoutMovements(false);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      if (attempt < 1) {
        await new Promise((r) => setTimeout(r, 600));
        hideLoading();
        return fetchSearchData(params, attempt + 1);
      }
      showAlert(
        'Error al cargar la liquidación. Compruebe red o vuelva a pulsar Buscar. En redes lentas puede requerir más de un intento.',
        'error',
      );
      resetSearchData();
    } finally {
      hideLoading();
    }
  };

  const mergeLiquidationSearchTarget = useCallback((collectId: string | number, date: string) => {
    setActiveSearch((prev) => {
      const next = { collectId, date };
      if (
        prev &&
        String(prev.collectId) === String(next.collectId) &&
        prev.date === next.date
      ) {
        return prev;
      }
      return next;
    });
  }, []);

  useEffect(() => {
    if (!activeSearch) return;
    fetchSearchData(activeSearch);
  }, [activeSearch]);

  useEffect(() => {
    if (!office_id || !collect_id) {
      setActiveSearch(null);
      resetSearchData();
      return;
    }

    if (isResolvingBox) {
      return;
    }

    mergeLiquidationSearchTarget(collect_id, currentDate);
  }, [office_id, collect_id, currentDate, isResolvingBox, mergeLiquidationSearchTarget]);

  // Handlers
  const handleReliquidar = async () => {
    if (!canUpdate) {
      showAlert('Sin permiso para reliquidar.', 'warning');
      return;
    }
    if (!canSearch) {
      showAlert('Debe seleccionar oficina y cobro', 'warning');
      return;
    }
    if (!box_id || isResolvingBox) {
      showAlert('Aún no hay caja disponible para este cobro. Intente en unos segundos.', 'warning');
      return;
    }
    const parsedCollectId = Number(collect_id);
    if (!Number.isInteger(parsedCollectId) || parsedCollectId <= 0) {
      showAlert('Seleccione un cobro válido', 'warning');
      return;
    }
    const dateStr = normalizeExpenseDate(currentDate);
    const ok = window.confirm(
      `¿Desea volver a calcular el cierre de este cobro para el día ${dateStr}?\n\n` +
        'El sistema actualizará otra vez los totales de ese día (como si el cierre aún no estuviera listo). Puede tardar un momento.',
    );
    if (!ok) return;

    setReliquidating(true);
    showLoading();
    try {
      await axios.post('/process/closer-reliquidate', {
        date: dateStr,
        collect_id: parsedCollectId,
      });
      showAlert('Listo: el cierre de ese día se volvió a calcular correctamente.', 'success');
      await fetchSearchData({ collectId: collect_id, date: currentDate });
    } catch (error: any) {
      const backendMessage = error?.response?.data?.message || error?.response?.data?.error;
      showAlert(backendMessage || 'No se pudo volver a calcular el cierre. Intente de nuevo.', 'error');
      console.error('Error reliquidando:', error);
    } finally {
      hideLoading();
      setReliquidating(false);
    }
  };
  const handleCollectChange = (nextCollectId: string) => {
    setCollect_id(nextCollectId);
    setBox_id(0);
    setActiveSearch(null);
    resetSearchData();

    if (!office_id || !nextCollectId) {
      return;
    }

    (async () => {
      const resolvedBoxId = await ensureBoxForCollect(nextCollectId);
      if (resolvedBoxId <= 0) {
        showAlert(
          'No fue posible resolver la caja del cobro. No se puede buscar ni reliquidar hasta corregir esto.',
          'error',
        );
        setActiveSearch(null);
        resetSearchData();
        return;
      }
      mergeLiquidationSearchTarget(nextCollectId, currentDate);
    })();
  };
  const handleOpenModal = () => {
    if (!canRegisterExpenses) {
      showAlert('Sin permiso para registrar gastos.', 'warning');
      return;
    }
    if (!hasActiveSearch) {
      showAlert('Debe buscar antes de registrar gastos', 'warning');
      return;
    }
    setModalOpen(true);
  };
  const handleModalConfirm = () => {
    if (!canCreate) {
      showAlert('Sin permiso para liquidar.', 'warning');
      return;
    }
    setModalConfirm(true);
  };
  const CloseModalConfirm = () => setModalConfirm(false);
  const handleCloseModal = () => setModalOpen(false);
  const displayedExpenses = useMemo(() => {
    return listExpenses?.[0]?.map(exp => ({
      id: exp.id,
      name: exp.expense.name,
      value: exp.value,
      description: exp.description,
      boxId: box_id,
      isLocal: false,
      expenseDate: exp.date,
    })) || [];
  }, [listExpenses, box_id]);
  const effectiveTotalExpenses = useMemo(() => {
    if (displayedExpenses.length > 0) {
      return displayedExpenses.reduce(
        (sum, expense) => sum + Number(expense.value ?? 0),
        0
      );
    }

    return totalExpenses;
  }, [displayedExpenses, totalExpenses]);
  const expectedCash = useMemo(() => {
    return valuePay + baseNumber - valueLend - effectiveTotalExpenses;
  }, [baseNumber, valuePay, valueLend, effectiveTotalExpenses]);
  const balance = useMemo(() => {
    return cashNumber - expectedCash;
  }, [expectedCash, cashNumber]);

  useEffect(() => {
    setCashShortOver(balance)
  }, [balance]);

  const resetExpenses = async (createdExpenses?: CreatedExpenseDisplayItem[]) => {
    if (!hasActiveSearch || !activeCollectId || !activeDate) return;
    if (createdExpenses && createdExpenses.length > 0) {
      const newItems: ExpenseItem[] = createdExpenses.map(exp => ({
        id: exp.id,
        date: normalizeExpenseDate(activeDate),
        description: exp.description,
        value: exp.value,
        balance: 0,
        expense: {
          id: exp.expense_id,
          name: exp.name,
          box_adjust: false,
        },
      }));

      setListExpenses(prev => {
        const currentItems = prev?.[0] || [];
        return [[...currentItems, ...newItems], currentItems.length + newItems.length];
      });
      if (box_id) {
        try {
          const closerRecord = await loadCloserByDate({
            collectId: activeCollectId,
            boxId: box_id,
            date: activeDate,
          });
          applyCloserData(closerRecord, { preserveManualFields: true });
          setDateWithoutMovements(
            liquidationDayHasNoMovements(closerRecord, [
              [...(listExpenses?.[0] || []), ...newItems],
              (listExpenses?.[0]?.length || 0) + newItems.length,
            ]),
          );
        } catch (error) {
          console.error('Error refrescando closer tras registrar gastos:', error);
          setDateWithoutMovements(false);
        }
      } else {
        setDateWithoutMovements(false);
      }
      return;
    }

    showLoading();
    try {
      setListExpenses(null);
      if (box_id) {
        const { data } = await axios.post(`/process/findexpensesbox`, {
          collect_id: activeCollectId,
          date: normalizeExpenseDate(activeDate),
        });
        setListExpenses(data);
        const closerRecord = await loadCloserByDate({
          collectId: activeCollectId,
          boxId: box_id,
          date: activeDate,
        });
        applyCloserData(closerRecord, { preserveManualFields: true });
        setDateWithoutMovements(liquidationDayHasNoMovements(closerRecord, data));
      }
    } catch (error) {
      showAlert('Error al recargar los gastos', 'error');
    } finally {
      hideLoading();
    }
  };

  const handleDeleteExpense = async (expenseId: number, expenseDateRaw?: string | null) => {
    if (!canDelete) {
      showAlert('Sin permiso para eliminar gastos.', 'warning');
      return;
    }
    if (deletingExpenseId === expenseId) {
      return;
    }
    const previousListExpenses = listExpenses;
    try {
      showLoading();
      const currentCollectId = collect_id || activeCollectId;
      const expenseRow = listExpenses?.[0]?.find(exp => Number(exp.id) === expenseId);
      const currentExpenseDate = expenseDateRaw || expenseRow?.date || currentDate || activeDate;
      const requestDate = currentExpenseDate ? normalizeExpenseDate(currentExpenseDate) : null;
      setDeletingExpenseId(expenseId);

      setListExpenses((prev) => {
        if (!prev) return prev;
        const updatedExpenses = prev[0].filter((exp) => Number(exp.id) !== expenseId);
        return [updatedExpenses, updatedExpenses.length];
      });

      await axios.delete(`/process/expensesbox/${expenseId}`);

      let expensesTupleForFlag: [ExpenseItem[], number] | null = null;
      if (currentCollectId && requestDate) {
        const { data } = await axios.post(`/process/findexpensesbox`, {
          collect_id: currentCollectId,
          date: requestDate,
        });
        setListExpenses(data);
        expensesTupleForFlag = data;
      }

      if (currentCollectId && requestDate && box_id) {
        const closerRecord = await loadCloserByDate({
          collectId: currentCollectId,
          boxId: box_id,
          date: requestDate,
        });
        applyCloserData(closerRecord, { preserveManualFields: true });
        if (expensesTupleForFlag) {
          setDateWithoutMovements(liquidationDayHasNoMovements(closerRecord, expensesTupleForFlag));
        }
      }

      showAlert('Gasto eliminado correctamente', 'success');
    } catch (error) {
      setListExpenses(previousListExpenses);
      showAlert('Error al eliminar el gasto', 'error');
      console.error('Error deleting expense:', error);
    } finally {
      setDeletingExpenseId(null);
      hideLoading();
    }
  };

  // 2. Modificar las columnas para incluir el botón de eliminar
  const expenseColumns: SimpleColumn[] = useMemo(() => {
    const baseColumns: SimpleColumn[] = [
      {
        field: 'name',
        headerName: 'Nombre del Gasto',
        minWidth: isMobile ? 100 : 320,
        flex: isMobile ? 1 : undefined,
        wrapText: true,
        renderCell: ({ row }) => (
          <Typography
            variant="body2"
            sx={row.isTotal ? { fontWeight: 600 } : undefined}
          >
            {row.name}
          </Typography>
        ),
      },
      {
        field: 'value',
        headerName: 'Valor',
        align: 'right',
        minWidth: isMobile ? 88 : 180,
        renderCell: ({ row }) => (
          <Typography
            variant="body2"
            sx={row.isTotal ? { fontWeight: 600 } : undefined}
          >
            {formatCurrency(Number(row.value ?? 0))}
          </Typography>
        ),
      },
      {
        field: 'actions',
        headerName: 'Opciones',
        align: 'center',
        minWidth: isMobile ? 72 : 140,
        renderCell: ({ row }) => {
          if (row.isTotal) return null;
          if (row.isLocal) return null;
          const expenseId = Number(row.id);
          if (Number.isNaN(expenseId)) return null;
          return (
            <PermissionButton
              allowed={canDelete}
              variant="outlined"
              color="error"
              size="small"
              tooltip="Eliminar"
              deniedTooltip="Sin permiso para eliminar gastos"
              disabled={deletingExpenseId === expenseId}
              onClick={(e) => {
                e.stopPropagation();
                handleDeleteExpense(expenseId, row.expenseDate as string | undefined);
              }}
            >
              {deletingExpenseId === expenseId ? (
                <CircularProgress size={16} color="inherit" />
              ) : isMobile ? 'X' : 'Eliminar'}
            </PermissionButton>
          );
        },
      },
    ];

    if (!isMobile) {
      baseColumns.splice(2, 0, {
        field: 'description',
        headerName: 'Descripción',
        minWidth: 260,
        wrapText: true,
        renderCell: ({ row }) => (
          <Typography variant="body2">{row.description || '-'}</Typography>
        ),
      });
    }

    return baseColumns;
  }, [isMobile, deletingExpenseId, canDelete, handleDeleteExpense]);


  // 3. Asegurarse de que la fila de total no tenga el botón de eliminar
  const rowsWithActions = [
    ...displayedExpenses.map((expense, index) => ({
      ...expense,
      id: typeof expense.id === 'number' ? expense.id : index + 1,
      isTotal: false,
    })),
    {
      id: 'total-row',
      name: 'Total:',
      value: effectiveTotalExpenses,
      description: '',
      isTotal: true
    }
  ];

  const submit = async (event: SyntheticEvent) => {
    event.preventDefault(); // Evita el envío del formulario por defecto
    const parsedBase = parseLocalizedNumber(base);
    const parsedCash = parseLocalizedNumber(cash);
    const parsedCollectId = Number(collect_id);
    let parsedUserId = Number(user_id);
    let parsedBoxId = Number(box_id);

    if (parsedBase === null || parsedCash === null) {
      showAlert('Base y efectivo deben ser números válidos', 'warning');
      return;
    }

    if (!Number.isInteger(parsedCollectId) || parsedCollectId <= 0) {
      showAlert('Debe seleccionar un cobro válido', 'warning');
      return;
    }

    const currentTime = moment().tz("America/Bogota").format("HH:mm:ss");
    const fullcurrentDate = moment.tz(
      `${currentDate} ${currentTime}`,
      "YYYY-MM-DD HH:mm:ss",
      "America/Bogota"
    ).format("YYYY-MM-DD HH:mm:ss");
    const formData = {
      date: fullcurrentDate,
      base: parsedBase,
      cash: parsedCash,
      expected_cash: expectedCash,
      percentage_route: percenCollectRoute,
      total_expenses: effectiveTotalExpenses,
      total_discounts: totalDiscount,
      total_off_exp_plus,
      total_off_exp_minus,
      total_pays: valuePay,
      total_lends: valueLend,
      total_utility,
      total_insurance_charge: totalInsuranceCharge,
      value_refinance,
      total_refinance: utility_refinance,
      total_cancel: valuePayCancel,
      count_pays: countPay,
      count_lends: countLend,
      count_cancel: countLendCancel,
      count_client: countClient,
      value_box,
      value_client_clove: valueClientClove,
      clove_client: countClientClove,
      cash_short_over: cashShortOver,
      collect_id: parsedCollectId,
      user_id: parsedUserId,
      route_id,
      box_id: parsedBoxId,
      closed: 1,
    };
    const url = "/process/routeclose";
    try {
      showLoading();
      if (!Number.isInteger(parsedUserId) || parsedUserId <= 0) {
        const { data } = await axios.get('user');
        parsedUserId = Number(data?.data?.id || 0);
      }
      if (!Number.isInteger(parsedBoxId) || parsedBoxId <= 0) {
        const boxResponse = await axios.get(`/boxcollect/${parsedCollectId}`);
        parsedBoxId = Number(boxResponse.data?.data?.[0]?.id || 0);
      }
      if (![parsedUserId, parsedBoxId].every(id => Number.isInteger(id) && id > 0)) {
        showAlert('No fue posible resolver usuario/caja para guardar la liquidación', 'warning');
        return;
      }

      const response = await axios.post(url, {
        ...formData,
        user_id: parsedUserId,
        box_id: parsedBoxId,
      });
      showAlert('Se ha realizado el registro exitosamente!', 'success');
      await new Promise(resolve => setTimeout(resolve, 1000));
      setModalConfirm(false)
      navigate("/procesos/liquidacioncobros");
    } catch (error) {
      const backendMessage = (error as any)?.response?.data?.message;
      showAlert(backendMessage || 'Error al guardar la liquidación', 'error');
      console.error('Error guardando liquidación:', error);
    } finally {
      hideLoading();
    }
  };

  return (
    <HeaderResponsive>
      <Box
        display="flex"
        flexWrap="wrap"
        justifyContent="space-between"
        alignItems="center"
        gap={1}
        mb={2}
        sx={{
          width: '100%',
          minWidth: 0,
          position: 'sticky',
          top: 0,
          zIndex: 6,
          backgroundColor: 'background.paper',
          py: 1,
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Box sx={{ minWidth: 0, flex: { xs: '1 1 100%', sm: '1 1 auto' } }}>
          <Title>Liquidar Cobro</Title>
        </Box>
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            gap: 1,
            width: { xs: '100%', sm: 'auto' },
            flexShrink: 0,
            alignItems: 'stretch',
          }}
        >
          <PermissionButton
            allowed={canUpdate}
            variant="outlined"
            color="warning"
            tooltip="Reliquidar"
            deniedTooltip="Sin permiso para reliquidar"
            startIcon={isMobile ? null : <AutorenewIcon />}
            onClick={handleReliquidar}
            disabled={!canOperateWithBox || reliquidating}
            sx={{ width: { xs: '100%', sm: 'auto' } }}
          >
            {reliquidating ? (
              <CircularProgress size={20} color="inherit" />
            ) : isMobile ? (
              <AutorenewIcon />
            ) : (
              'Reliquidar'
            )}
          </PermissionButton>
          <PermissionButton
            allowed={canCreate}
            variant="contained"
            tooltip="Liquidar"
            deniedTooltip="Sin permiso para liquidar"
            startIcon={isMobile ? null : <SaveIcon />}
            onClick={handleModalConfirm}
            sx={{ width: { xs: '100%', sm: 'auto' } }}
          >
            {isMobile ? <SaveIcon /> : 'Liquidar'}
          </PermissionButton>
        </Box>
      </Box>
      <CardContent sx={{ px: { xs: 1, sm: 2 }, py: { xs: 1, sm: 2 }, '&:last-child': { pb: { xs: 1, sm: 2 } } }}>
        <Grid container spacing={{ xs: 2, sm: 3 }}>

          <Grid item xs={12} sm={6} lg={4}>
            <TextField
              fullWidth
              color={dateWithoutMovements ? 'error' : undefined}
              label="Fecha"
              type="date"
              size="small"
              value={currentDate}
              onChange={(e) => setCurrentDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={
                dateWithoutMovements
                  ? {
                      '& .MuiInputBase-root': {
                        backgroundColor: theme.palette.error.light,
                      },
                    }
                  : undefined
              }
            />
          </Grid>
          <Grid item xs={12} sm={6} lg={4}>
            <FormControl fullWidth>
              <InputLabel>Seleccionar Oficina</InputLabel>
              {offices.length > 0 && (
                <Select
                  label="Seleccionar Oficina"
                  value={office_id}
                  size="small"
                  onChange={(e) => setOffice_id(e.target.value)}
                  required
                >
                  {offices.map((office: Office) => (
                    <MenuItem key={office.id} value={office.id}>
                      {office.name}
                    </MenuItem>
                  ))}
                </Select>
              )}
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6} lg={4}>
            <FormControl fullWidth>
              <InputLabel>Seleccionar Cobro</InputLabel>
              {collectsOffice.length > 0 && (
                <Select
                  label="Seleccionar Cobro"
                  value={collect_id}
                  size="small"
                  onChange={(e) => handleCollectChange(String(e.target.value))}
                  required
                >
                  {collectsOffice.map((collect: Collect) => (
                    <MenuItem key={collect.id} value={collect.id}>
                      {collect.name}
                    </MenuItem>
                  ))}
                </Select>
              )}
            </FormControl>
          </Grid>
        </Grid>
      </CardContent>
      <PageScrollArea
        sx={{
          px: { xs: 1, sm: 2 },
          py: { xs: 1, sm: 2 },
          maxWidth: '100%',
          minWidth: 0,
          overflowX: 'hidden',
          maxHeight: { xs: 'none', md: 'calc(100vh - 250px)' },
          overflowY: { xs: 'visible', md: 'auto' },
          '&:last-child': { pb: { xs: 2, sm: 3 } },
        }}
      >
          <Box sx={{ mb: { xs: 2, sm: 4 } }}>
            <Accordion
              expanded={expensesAccordionOpen}
              onChange={(_, next) => setExpensesAccordionOpen(next)}
              disableGutters
              elevation={0}
              sx={{
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                overflow: 'hidden',
                '&:before': { display: 'none' },
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon aria-label={expensesAccordionOpen ? 'Contraer gastos' : 'Expandir gastos'} />}
                sx={{
                  px: { xs: 1, sm: 2 },
                  minHeight: 56,
                  '& .MuiAccordionSummary-content': {
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    gap: 1,
                    my: 1,
                  },
                }}
              >
                <Typography variant={isMobile ? 'subtitle1' : 'h6'} component="h2" sx={{ fontWeight: 600 }}>
                  Gastos del Cobro
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  Total: {formatCurrency(effectiveTotalExpenses)}
                </Typography>
                {hasActiveSearch && (
                  <PermissionButton
                    allowed={canRegisterExpenses}
                    variant="contained"
                    size="small"
                    tooltip="Registrar gastos"
                    deniedTooltip="Sin permiso para registrar gastos"
                    startIcon={isMobile ? null : <AddBoxIcon />}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleOpenModal();
                    }}
                    sx={{ ml: { xs: 0, sm: 'auto' }, width: { xs: '100%', sm: 'auto' } }}
                  >
                    {isMobile ? <AddBoxIcon /> : 'Registrar gasto'}
                  </PermissionButton>
                )}
              </AccordionSummary>
              <AccordionDetails sx={{ px: { xs: 1, sm: 2 }, pt: 0, pb: 2 }}>
                {(listExpenses && listExpenses[0] && listExpenses[0].length > 0) ? (
                  <Box sx={{ maxWidth: '100%', minWidth: 0, overflowX: 'auto' }}>
                    <SimpleCustomTable
                      rows={rowsWithActions}
                      columns={expenseColumns}
                      maxHeight={isMobile ? 220 : 360}
                      showBorders
                      wrapCellContent
                    />
                  </Box>
                ) : (
                  <Typography variant="body2" color="text.secondary" textAlign="center" py={3}>
                    No hay gastos registrados para esta fecha y cobro.
                  </Typography>
                )}
              </AccordionDetails>
            </Accordion>
          </Box>

          <Divider sx={{ my: { xs: 2, sm: 4 } }} />

          <Box>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  color="error"
                  label="Base"
                  type="number"
                  size="small"
                  value={base}
                  sx={{
                    "& .MuiInputBase-root": {
                      backgroundColor: isBaseNonZero
                        ? theme.palette.error.light
                        : "transparent",
                    },
                  }}
                  onFocus={() => {
                    if (base === "0") setBase("");
                  }}
                  onChange={(e) => setBase(e.target.value)}
                  onBlur={(e) => {
                    if (e.target.value.trim() === "") {
                      setBase("0");
                    }
                  }}
                  inputProps={{ min: 0 }}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  color="error"
                  label="Efectivo"
                  type="number"
                  size="small"
                  value={cash}
                  sx={{
                    "& .MuiInputBase-root": {
                      backgroundColor: isCashNonZero
                        ? theme.palette.error.light
                        : "transparent",
                    },
                  }}
                  onFocus={() => {
                    if (cash === "0") setCash("");
                  }}
                  onChange={(e) => setCash(e.target.value)}
                  onBlur={(e) => {
                    if (e.target.value.trim() === "") {
                      setCash("0");
                    }
                  }}
                  inputProps={{ min: 0 }}
                />
              </Grid>
            </Grid>
          </Box>

          <Divider sx={{ my: { xs: 2, sm: 4 } }} />

          <TableContainer
            component={Paper}
            sx={{
              overflowX: 'auto',
              maxWidth: '100%',
              WebkitOverflowScrolling: 'touch',
            }}
          >
            <Table
              sx={{
                width: '100%',
                minWidth: isMobile ? 0 : 280,
                tableLayout: isMobile ? 'fixed' : 'auto',
                '& .MuiTableCell-root': { py: isMobile ? 0.5 : 0.75 },
              }}
              size="small"
              aria-label="liquidación tabla"
            >
              <TableHead>
                <TableRow>
                  <TableCell
                    align="center"
                    colSpan={4}
                    sx={{
                      backgroundColor: '#093d70',
                      color: 'white',
                      fontWeight: 'bold',
                      py: 1,
                      fontSize: isMobile ? '0.75rem' : '0.875rem',
                      px: isMobile ? 0.5 : 2,
                    }}
                  >
                    Liquidación Ruta
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  <TableCell sx={compactTableLabelSx}>Número Abonos:</TableCell>
                  <TableCell sx={compactTableCellSx} align="left">{countPay}</TableCell>
                  <TableCell sx={compactTableLabelSx}>Valor Abonos:</TableCell>
                  <TableCell sx={compactTableCellSx} align="right">{formatCurrency(valuePay)}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={compactTableLabelSx}>Número Préstamos:</TableCell>
                  <TableCell sx={compactTableCellSx} align="left">{countLend}</TableCell>
                  <TableCell sx={compactTableLabelSx}>Valor Préstamos:</TableCell>
                  <TableCell sx={compactTableCellSx} align="right">{formatCurrency(valueLend)}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={compactTableLabelSx}>Cancelaciones:</TableCell>
                  <TableCell sx={compactTableCellSx} align="left">{countLendCancel}</TableCell>
                  <TableCell sx={compactTableLabelSx}>Valor Cancelaciones:</TableCell>
                  <TableCell sx={compactTableCellSx} align="right">{formatCurrency(valueLendCancel)}</TableCell>
                </TableRow>
                <TableRow sx={{ backgroundColor: '#fff3e0' }}>
                  <TableCell sx={compactTableLabelSx} colSpan={2}><strong>Descuentos:</strong></TableCell>
                  <TableCell sx={compactTableCellSx} colSpan={2} align="right">{formatCurrency(totalDiscount)}</TableCell>
                </TableRow>
                <TableRow sx={{ backgroundColor: '#fff3e0' }}>
                  <TableCell sx={compactTableLabelSx} colSpan={2}><strong>Gastos Gestión:</strong></TableCell>
                  <TableCell sx={compactTableCellSx} colSpan={2} align="right">{formatCurrency(totalInsuranceCharge)}</TableCell>
                </TableRow>
                <TableRow sx={{ backgroundColor: '#fff3e0' }}>
                  <TableCell sx={compactTableLabelSx} colSpan={2}><strong>Gastos:</strong></TableCell>
                  <TableCell sx={compactTableCellSx} colSpan={2} align="right">{formatCurrency(effectiveTotalExpenses)}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={compactTableLabelSx} colSpan={2}>Efectivo Esperado:</TableCell>
                  <TableCell sx={compactTableCellSx} colSpan={2} align="right">{formatCurrency(expectedCash)}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={compactTableLabelSx} colSpan={2}>Clientes Ruta Con Préstamo:</TableCell>
                  <TableCell sx={compactTableCellSx} colSpan={2} align="right">{countClient}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={compactTableLabelSx} colSpan={2}>% Cobro de la Ruta:</TableCell>
                  <TableCell sx={compactTableCellSx} colSpan={2} align="right">{percenCollectRoute}%</TableCell>
                </TableRow>
                <TableRow sx={{ backgroundColor: '#ffebee' }}>
                  <TableCell sx={compactTableLabelSx} colSpan={2}><strong>Descuadre:</strong></TableCell>
                  <TableCell sx={compactTableCellSx} colSpan={2} align="right">{formatCurrency(balance)}</TableCell>
                </TableRow>
                <TableRow sx={{ backgroundColor: '#f3e5f5' }}>
                  <TableCell sx={compactTableLabelSx} colSpan={1}>Clientes Clavo:</TableCell>
                  <TableCell sx={compactTableLabelSx} colSpan={1}>{countClientClove}</TableCell>
                  <TableCell sx={compactTableCellSx} colSpan={2} align="right">{formatCurrency(valueClientClove)}</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
      </PageScrollArea>

      <ExpenseCollect
        isOpen={modalOpen}
        onClose={handleCloseModal}
        collect_id={collect_id}
        boxId={Number(box_id)}
        userId={user_id}
        selectedDate={currentDate}
        showLoading={showLoading}
        hideLoading={hideLoading}
        canSave={canRegisterExpenses}
        onAfterSubmit={resetExpenses}
      />
      <ConfirmationModal
        isOpen={modalOpenConfirm}
        onClose={CloseModalConfirm}
        onConfirm={submit}
      />
    </HeaderResponsive>
  );
};

export default LiquidacionCobros;
